/**
 * Every Persian string «پیشنهادها» draws that the server does not send.
 *
 * Tip titles, bodies and CTA labels are the server's (they carry live counts
 * and dates); what lives here is the frame around them — the menu, the undo
 * row, the hub's headings and notices, and the reasons a control is blocked.
 * One module, so a copy review reads one file and the copy tests sweep one
 * object. Vocabulary follows the section's own words: «پیامک گروهی», «گروه‌های
 * بیماران»; never «کمپین», «سگمنت», «هوشمند» or «دستیار».
 */

export const GUIDE_TITLE = 'پیشنهادها';

/** Blocked while a support session views the panel as this user. */
export const READ_ONLY_REASON =
  'در حالت ورود به‌جای کاربر، پیشنهادها فقط نمایش داده می‌شن. خود کاربر باید از حساب خودش این کار رو انجام بده.';

export const MENU = {
  snoozeDay: 'نمایش دوباره در روز بعد',
  snoozeWeek: 'نمایش دوباره در هفتهٔ بعد',
  notRelevant: 'به کار ما نمی‌آد',
  markDone: 'انجام دادم',
  markDoneClinic: 'انجام شد؛ برای همهٔ همکاران',
} as const;

export const moreLabel = (title: string): string => `کارهای دیگه برای «${title}»`;
export const DISMISS_LABEL = 'بستن این پیشنهاد';
export const WHY_LABEL = 'چرا؟';

export const UNDO = {
  SNOOZE_DAY: 'تا فردا نشونش نمی‌دیم.',
  SNOOZE_WEEK: 'تا هفتهٔ بعد نشونش نمی‌دیم.',
  DISMISS: 'این پیشنهاد بسته شد.',
  /** The second «×» on the same tip retires it (the shared fold). */
  DISMISS_RETIRES:
    'این پیشنهاد دیگه نمایش داده نمی‌شه. برای برگردوندنش، بخش «کنار گذاشته‌ها» رو باز کنید.',
  NOT_RELEVANT:
    'این پیشنهاد دیگه نمایش داده نمی‌شه. برای برگردوندنش، بخش «کنار گذاشته‌ها» رو باز کنید.',
  MARK_DONE_CLINIC: 'برای همهٔ همکاران ثبت شد.',
  MARK_DONE: 'انجام این کار ثبت شد.',
  restore: 'بازگرداندن',
} as const;

export const CTA = {
  preparingDraft: 'در حال آماده‌سازی پیش‌نویس…',
  stale: 'این پیشنهاد دیگر به‌روز نیست.',
  copied: 'متن کپی شد. اون رو در پیام‌رسان کلینیک جای‌گذاری کنید.',
  copyFailed: 'کپی انجام نشد. دوباره امتحان کنید.',
} as const;

/** `GuidePrefillNotice`: a form a suggestion filled in. */
export const PREFILL = {
  title: 'این فرم از روی یک پیشنهاد پر شده',
  body: (fields: string, submit: string): string =>
    `${fields} رو بررسی کنید و هرجا لازمه تغییر بدید. تا «${submit}» رو نزنید، چیزی ثبت نمی‌شه.`,
} as const;

export const CARD = {
  all: 'همه',
  occasionChip: (days: string, name: string): string => `${days} روز تا ${name}`,
  occasionToday: (name: string): string => `امروز ${name} است`,
} as const;

export const HUB = {
  quiet: {
    FIRST_SESSION: 'امروز فقط با بوکورا آشنا بشید؛ از فردا پیشنهادها رو روی داشبورد هم می‌بینید.',
    PAUSED_LEAD: 'پیشنهادها تا',
    PAUSED_TAIL: 'نشان داده نمی‌شن.',
    OFF: 'پیشنهادها فقط همین‌جا نشان داده می‌شن.',
    CLINIC_DISABLED: 'مالک کلینیک پیشنهادها رو برای همه خاموش کرده است.',
    /** Billing read-only (an ended trial, an unpaid invoice). */
    CLINIC_READ_ONLY:
      'فعلاً فقط می‌تونید اطلاعات کلینیک رو ببینید. بعد از تمدید اشتراک، پیشنهادها دوباره روی داشبورد نمایش داده می‌شن و می‌تونید کارهای پیشنهادی رو انجام بدید.',
    /**
     * Two «×» in a row earn a quiet period (`TIP_BACKOFF`: a week, then two);
     * «از سرگیری» ends it. With its end date: «… تا <JalaliDate> کمتر …».
     */
    BACKOFF: 'چند پیشنهاد رو پشت سر هم بستید؛ فعلاً کمتر نشونتون می‌دیم.',
    BACKOFF_LEAD: 'چند پیشنهاد رو پشت سر هم بستید؛ تا',
    BACKOFF_TAIL: 'کمتر نشونتون می‌دیم.',
  },
  resume: 'از سرگیری',
  now: 'پیشنهادهای فعلی',
  emptyTitle: 'فعلاً پیشنهاد جدیدی نداریم',
  emptyBody: 'پیشنهادهای جدید بر اساس اطلاعات کلینیک در این بخش نمایش داده می‌شن.',
  occasions: 'مناسبت‌های پیش رو',
  occasionsEmpty: 'در دو ماه آینده مناسبتی برای پیامک گروهی نداریم.',
  daysLeft: (days: string): string => `${days} روز دیگر`,
  today: 'امروز',
  tagHoliday: 'تعطیل رسمی',
  tagHard: 'سوگواری — بدون پیامک تبلیغاتی',
  tagSoft: 'لحن آرام',
  later: 'بعداً',
  laterSummary: (n: string): string => `${n} پیشنهاد به تعویق افتاده`,
  showNow: 'نمایش الان',
  setAside: 'کنار گذاشته‌ها',
  setAsideSummary: (n: string): string => `${n} پیشنهاد کنار گذاشته`,
  showAgain: 'نمایش دوباره',
  done: 'انجام شد',
  settings: 'تنظیمات پیشنهادها',
  mode: 'نمایش پیشنهادها',
  modeOn: 'روشن',
  modeDay: 'تا فردا نمایش داده نشه',
  modeWeek: 'تا هفتهٔ بعد نمایش داده نشه',
  modeOff: 'فقط هنگام باز کردن این بخش',
  categories: 'چه پیشنهادهایی رو ببینم؟',
  clinicWide: 'برای کل کلینیک',
  clinicSwitch: 'پیشنهادها برای همهٔ همکاران روشن باشد',
  clinicSwitchReason:
    'برای فعال یا غیرفعال کردن پیشنهادها برای کل کلینیک، دسترسی «ویرایش تنظیمات» لازم است. از مالک کلینیک بخواید اون رو در «کارکنان و نقش‌ها» به نقش شما بده.',
  hiddenRules: 'پیشنهادهای پنهان برای همه',
  hiddenRulesEmpty: 'هیچ پیشنهادی برای همه پنهان نشده است.',
  hideForAll: 'برای همهٔ همکاران پنهان شود',
  hideForAllConfirm: 'این پیشنهاد برای همهٔ همکاران پنهان شود؟',
  hideForAllContent:
    'تا وقتی از همین‌جا «نمایش دوباره» رو نزنید، هیچ‌کس در کلینیک اون رو نمی‌بینه.',
  hideForAllOk: 'پنهان شود',
  cancel: 'انصراف',
  visibleRules: 'پیشنهادهای قابل نمایش',
  readOnlyHelper: 'در حالت ورود به‌جای کاربر، تنظیمات پیشنهادها تغییر نمی‌کنه.',
} as const;
